// @flow

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Table, Badge } from 'reactstrap';
import { withRouter } from 'react-router';
import StyleClasses from '../../styles/StyleClasses';
import LoadingContainer from '../basicElements/LoadingContainer';
import { jsonGetRequest } from '../../store/requests';

type SessionData = {
    id: string,
    user_id: string,
    location_id: string,
    first_check_in_time: number,
    last_check_in_time: number,
    last_check_out_time: ?number,
    invalidated: boolean,
    is_appclip_user: boolean,
    device_id: ?string,
    user_name: string,
    bill_id: ?string,
    bill_status: ?number,
    is_paid: boolean,
    expiration: number,
};

type PropsType = {
    userId: string,
    history: any,
};

type StateType = {
    sessions: ?Array<SessionData>,
    loading: boolean,
    error: ?string,
};

function formatTimestamp(ts: ?number): string {
    if (!ts) return '—';
    const d = new Date(ts * 1000);
    return d.toLocaleString();
}

function isSessionActive(session: SessionData): boolean {
    const now = Math.floor(Date.now() / 1000);
    return !session.invalidated && session.expiration > now;
}

class UserSessionsPanel extends Component<PropsType, StateType> {
    constructor(props: PropsType) {
        super(props);
        this.state = {
            sessions: null,
            loading: true,
            error: null,
        };
    }

    componentDidMount() {
        jsonGetRequest(`users/${this.props.userId}/sessions`)
            .then((sessions) => {
                this.setState({ sessions, loading: false });
            })
            .catch((err) => {
                this.setState({ error: err.toString(), loading: false });
            });
    }

    handleSessionClick = (sessionId: string) => {
        this.props.history.push(`/location_session-info/${sessionId}`);
    };

    render() {
        if (this.state.loading) {
            return (
                <div className={StyleClasses.spacedElement}>
                    <h4>Sessions</h4>
                    <LoadingContainer />
                </div>
            );
        }

        if (this.state.error) {
            return (
                <div className={StyleClasses.spacedElement}>
                    <h4>Sessions</h4>
                    <p style={{ color: 'red' }}>Failed to load sessions: {this.state.error}</p>
                </div>
            );
        }

        const sessions = this.state.sessions || [];

        if (sessions.length === 0) {
            return (
                <div className={StyleClasses.spacedElement}>
                    <h4>Sessions</h4>
                    <p>No sessions found.</p>
                </div>
            );
        }

        // Group by device_id
        const grouped: { [string]: Array<SessionData> } = {};
        sessions.forEach((s) => {
            const key = s.device_id || 'unknown';
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(s);
        });

        const deviceIds = Object.keys(grouped);

        return (
            <div className={StyleClasses.spacedElement}>
                <h4>Sessions ({sessions.length})</h4>
                {deviceIds.map((deviceId) => (
                    <div key={deviceId} style={{ marginBottom: '1.5rem' }}>
                        <h6>
                            Device: <code>{deviceId}</code>
                        </h6>
                        <Table hover size="sm">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th>First Check In</th>
                                    <th>Last Check In</th>
                                    <th>Check Out</th>
                                    <th>Bill</th>
                                    <th>Session ID</th>
                                </tr>
                            </thead>
                            <tbody>
                                {grouped[deviceId].map((session) => (
                                    <tr
                                        key={session.id}
                                        onClick={() => this.handleSessionClick(session.id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td>
                                            {session.is_appclip_user ? (
                                                <Badge color="warning">App Clip</Badge>
                                            ) : (
                                                <Badge color="primary">App</Badge>
                                            )}
                                        </td>
                                        <td>
                                            {isSessionActive(session) ? (
                                                <Badge color="success">Active</Badge>
                                            ) : session.invalidated ? (
                                                <Badge color="secondary">Invalidated</Badge>
                                            ) : (
                                                <Badge color="dark">Expired</Badge>
                                            )}
                                        </td>
                                        <td>{formatTimestamp(session.first_check_in_time)}</td>
                                        <td>{formatTimestamp(session.last_check_in_time)}</td>
                                        <td>{formatTimestamp(session.last_check_out_time)}</td>
                                        <td>
                                            {session.bill_id ? (
                                                <Link
                                                    to={`/bill-info/${session.bill_id}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {session.is_paid ? (
                                                        <Badge color="success">Paid</Badge>
                                                    ) : (
                                                        <Badge color="info">Open</Badge>
                                                    )}
                                                </Link>
                                            ) : (
                                                <span style={{ color: '#999' }}>—</span>
                                            )}
                                        </td>
                                        <td>
                                            <small>{session.id}</small>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                ))}
            </div>
        );
    }
}

export default withRouter(UserSessionsPanel);
